import { Benefit } from 'all-in-one-shared-api';
import { Card } from 'antd';
import { FormInput, FormWrapper } from 'components/form';
import { type FC, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { isLocaleEnglish } from 'utils/languageUtils';
import { SelectedClaimTypeObjectType } from '../hooks/useSelectedClaimTypeMapping';
import { CardTitle } from 'components/claimCard/CardTitle';
import { CurrencySelect } from './CurrencySelect';
import { breakpoint } from 'styled-components-breakpoint';
import { useFormContext } from 'react-hook-form';
import { revalidateReactFormHook } from '../../../i18n';

interface ClaimAmountCard {
  formStepIndex?: number;
  selectedClaimTypeObject: SelectedClaimTypeObjectType;
}

const Wrapper = styled.div`
  display: grid;
  row-gap: 24px;
  ${breakpoint('desktop')`
    width: 50%;
  `}
`;

export function getBenefitLocaleDescription(isEnglish: boolean, benefit?: Benefit) {
  if (!benefit) return null;
  return isEnglish ? benefit.description : benefit.chineseDescription;
}

const ClaimAmountCard: FC<ClaimAmountCard> = ({ formStepIndex = 3, selectedClaimTypeObject }) => {
  const { t, i18n } = useTranslation();
  const methods = useFormContext();
  const { getValues, setValue, watch } = methods;
  const claimTypeObject = watch('claimTypeObject');
  const previousClaimTypeObject = useRef<string[]>(claimTypeObject);
  const changeReceiptAmountsBasedOnSelectedClaims = useCallback(
    (previousClaimTypes: string[], currentClaimTypes: string) => {
      if (!previousClaimTypes || previousClaimTypes.length === currentClaimTypes.length) {
        return;
      }
      previousClaimTypes.forEach((claimType, index) => {
        const foundClaimType = currentClaimTypes.includes(claimType);
        if (!foundClaimType) {
          const receiptAmounts: string[] = getValues('receiptAmounts');
          receiptAmounts.splice(index, 1);
          setValue('receiptAmounts', receiptAmounts);
        }
      });
      revalidateReactFormHook(methods);
    },
    [setValue, getValues, methods],
  );

  const resetReceiptAmounts = useCallback(() => {
    let receiptAmounts = getValues('receiptAmounts');
    if (receiptAmounts?.length === selectedClaimTypeObject.data.length) {
      // Notes: When re-generate Controller the value should be empty or 0, but passing undefined as value, the value will be old data
      receiptAmounts = receiptAmounts.map((receiptAmount: string | undefined) =>
        receiptAmount === undefined ? '' : receiptAmount,
      );
      setValue('receiptAmounts', receiptAmounts);
    }
    if (selectedClaimTypeObject.data?.length === 0) {
      if (receiptAmounts?.length === 0) {
        setValue('receiptAmounts', ['']);
      }
    }
  }, [getValues, selectedClaimTypeObject.data.length, setValue]);

  useEffect(() => {
    changeReceiptAmountsBasedOnSelectedClaims(previousClaimTypeObject.current, claimTypeObject);
    resetReceiptAmounts();
    previousClaimTypeObject.current = claimTypeObject;
  }, [changeReceiptAmountsBasedOnSelectedClaims, claimTypeObject, resetReceiptAmounts]);
  const _renderOutPatientInputs = () =>
    selectedClaimTypeObject.data.map((value, index) => (
      <FormWrapper
        name={`receiptAmounts.${index}`}
        label={t('newClaim.paymentInfo.benefitAmountLabel', {
          benefit: getBenefitLocaleDescription(isLocaleEnglish(i18n.language), value),
        })}
        key={value.code}
      >
        <FormInput
          name={`receiptAmounts.${index}`}
          isCurrencyInput
          placeholder={t('newClaim.paymentInfo.benefitPlaceholder')}
          addonAfter="$"
        />
      </FormWrapper>
    ));

  const _renderOtherTypeInput = () => (
    <FormWrapper
      name={`receiptAmounts.0`}
      label={t('newClaim.paymentInfo.benefitAmountLabel', {
        benefit: getBenefitLocaleDescription(isLocaleEnglish(i18n.language), selectedClaimTypeObject.data[0]),
      })}
    >
      <FormInput
        name={`receiptAmounts.0`}
        isCurrencyInput
        placeholder={t('newClaim.paymentInfo.benefitPlaceholder')}
        addonAfter="$"
      />
    </FormWrapper>
  );

  return (
    <Card
      id="claimAmount"
      title={<CardTitle formStepIndex={formStepIndex} title={t('newClaim.paymentInfo.receiptInfo')} />}
    >
      <Wrapper>
        <CurrencySelect name="currency" label={t('newClaim.paymentInfo.receiptCurrencyLabel')} />
        {selectedClaimTypeObject.type === 'OP' && _renderOutPatientInputs()}
        {selectedClaimTypeObject.type !== 'OP' && _renderOtherTypeInput()}
      </Wrapper>
    </Card>
  );
};

export default ClaimAmountCard;
